import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from '@themesberg/react-bootstrap';
import Joi from 'joi';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Datetime from 'react-datetime';
import { useTranslation } from 'react-i18next';
import { returnUserReadableDate } from '../../utils/datetime';
import { generateRandomId } from '../../utils/string';
import { returnValidationErrorMessages } from '../../utils/validation';
import DropdownComponent from './Dropdown';
import { getAdditionalParcelInInvoiceToReschedule } from '../../utils/parcels';
import { getAvailableSerialsForReplacement } from '../../parse-functions/parcels';

const defectivePickupRescheduleSchema = Joi.object({
  targetBranch: Joi.string().required(),
  date: Joi.date().required(),
  remarks: Joi.string().required(),
});

const replacementPendingRescheduleSchema = Joi.object({
  targetBranch: Joi.string().required(),
  date: Joi.date().required(),
  remarks: Joi.string().required(),
  newSerialNo: Joi.string().optional().allow(''),
});
const DefectivePickupRescheduleModal = (props) => {
  const {
    showModal,
    closeModal,
    onConfirm,
    selectedParcelId,
    // Optional: the actual selected parcel object. Some pages (e.g. delivery-data)
    // keep parcels in LOCAL state, not state.parcels[pageStatus], so the Redux
    // lookup below can't resolve it (selectedParcel ends up {} and itemNumber
    // undefined → the serial picker can't key into availableSerials). Those pages
    // pass the parcel directly.
    selectedParcelData,
    pageStatus,
    targetDropdownOptions,
    disableTargetDropdown = true,
    getNewSerialNo = false,
    errors = [],
  } = props;
  const [formErrors, setFormErrors] = useState({});
  const pageParcels = useSelector((state) => state.parcels[pageStatus] || {});
  const selectedParcel =
    selectedParcelData || pageParcels[selectedParcelId] || {};
  const [data, setFormData] = useState({});
  const configData = useSelector((state) => state.user.config || {});
  const [extraParcelIds, setExtraParcelIds] = useState([]);
  const [extraParcelIdSerialNos, setExtraParcelIdSerialNos] = useState({});
  const [extraSerialNumberError, setExtraSerialNumberError] = useState({});
  const [isNonSerialNumberProduct, setIsNonSerialNumberProduct] = useState({});
  const warehouse = useSelector((state) => state?.user?.warehouse || {});
  // Wondersoft serial picker: in-stock serials keyed by itemNumber. When the
  // store isn't wondersoft-onboarded, `isWondersoftStore` stays false and the
  // fields fall back to free-text entry (Wings / non-wondersoft).
  const [isWondersoftStore, setIsWondersoftStore] = useState(false);
  const [availableSerials, setAvailableSerials] = useState({});
  // The target branch chosen in the dropdown — replacement stock is pulled from
  // THIS branch's Wondersoft inventory (not the logged-in user's warehouse).
  const [targetWarehouse, setTargetWarehouse] = useState(null);
  // True while getAvailableSerialsForReplacement is in flight (multiple eShopaid
  // GetInventory calls) — drives an inline spinner so the operator knows serials
  // are loading instead of seeing a stale free-text box.
  const [loadingSerials, setLoadingSerials] = useState(false);

  const { t } = useTranslation();
  const handleConfirmClick = async () => {
    let schemaName = getNewSerialNo
      ? replacementPendingRescheduleSchema
      : defectivePickupRescheduleSchema;
    if (extraParcelIds?.length > 0 && getNewSerialNo) {
      let updatedSerialNumberError = {};
      // user has selected that the extra parcels are being rescheduled. We need to validate their serial no
      // eslint-disable-next-line no-unused-expressions
      extraParcelIds?.forEach((extraParcelId) => {
        if (
          !isNonSerialNumberProduct[extraParcelId] &&
          !extraParcelIdSerialNos[extraParcelId]
        ) {
          updatedSerialNumberError[extraParcelId] =
            'Please enter serial number';
        }
      });

      if (Object.keys(updatedSerialNumberError)?.length > 0) {
        setExtraSerialNumberError(updatedSerialNumberError);
        return;
      } else {
        setExtraSerialNumberError({});
      }
    }
    let { error: formErrors = {}, value: formValue } = schemaName.validate(
      data,
      {
        abortEarly: false,
        allowUnknown: true,
      }
    );
    if (getNewSerialNo && !isNonSerialNumberProduct[selectedParcelId]) {
      if (!formValue?.newSerialNo) {
        formErrors = {
          ...formErrors,
          details: [
            ...(formErrors.details || []),
            {
              message: 'Please enter the new serial number',
              path: ['newSerialNo'],
              type: 'manual',
              context: { key: 'newSerialNo' },
            },
          ],
        };
      }
    }
    const { details = {} } = formErrors;
    if (details?.length > 0) {
      const validationErrorMessages = returnValidationErrorMessages(details);
      setFormErrors(validationErrorMessages);
    } else {
      // again check for the conditional newSerialNo
      if (
        getNewSerialNo &&
        !isNonSerialNumberProduct[selectedParcelId] &&
        !formValue?.newSerialNo
      ) {
        setFormErrors((prev) => ({
          ...prev,
          newSerialNo: 'Please enter the new serial number',
        }));
      } else {
        setFormErrors({});
        onConfirm({
          ...formValue,
          isNonSerialNumberProduct: isNonSerialNumberProduct,
          newSerialNo: formValue?.newSerialNo,
          parcelObjectId: selectedParcelId,
          extraParcelIds,
          extraParcelIdSerialNos: extraParcelIdSerialNos,
          // The dropdown-selected target branch. Its Wondersoft store holds the
          // replacement stock, so the serial-swap bin move must run against it —
          // not the logged-in user's warehouse.
          targetWarehouse,
        });
      }
    }
  };
  const additionalParcelToChoose = getAdditionalParcelInInvoiceToReschedule(
    configData?.productCategoryToCheckForReschedule || [],
    pageParcels,
    selectedParcel
  );
  useEffect(() => {
    setIsNonSerialNumberProduct({
      [selectedParcelId]: false,
    });
  }, [selectedParcelId]);
  useEffect(() => {
    console.log('errors', errors);
    if (errors.length > 0) {
      let extraItemsError = {};
      errors.forEach((item) => {
        const [parcelId, errorMsg] = Object.entries(item)[0];
        console.log(parcelId, errorMsg, item, Object.entries(item)[0]);
        if (parcelId === selectedParcelId) {
          setFormErrors((prev) => ({
            ...prev,
            newSerialNo: errorMsg,
          }));
        } else {
          extraItemsError[parcelId] = errorMsg;
        }
      });
      if (Object.keys(extraItemsError).length > 0) {
        setExtraSerialNumberError(extraItemsError);
      }
    }
  }, [errors]);

  // Fetch in-stock serials for the picker when the replacement modal opens.
  // Only the replacement flow (getNewSerialNo) needs a new serial; for a
  // non-wondersoft store the backend returns onboarded:false and we keep
  // free-text entry.
  useEffect(() => {
    let cancelled = false;
    const loadSerials = async () => {
      if (!showModal || !getNewSerialNo || !selectedParcelId) {
        return;
      }
      // Pull replacement stock from the SELECTED target branch (the branch that
      // fulfils the replacement) — NOT the logged-in user's warehouse. Until a
      // target branch is chosen there's nothing to query, so keep free-text.
      if (!targetWarehouse?.shortCode && !targetWarehouse?.warehouseCode) {
        setIsWondersoftStore(false);
        setAvailableSerials({});
        setLoadingSerials(false);
        return;
      }
      setLoadingSerials(true);
      try {
        const resp = await getAvailableSerialsForReplacement({
          shortCode: targetWarehouse?.shortCode,
          warehouseCode: targetWarehouse?.warehouseCode,
          parcelObjectId: selectedParcelId,
          extraParcelIds,
        });
        if (cancelled) return;
        setIsWondersoftStore(!!resp?.onboarded);
        setAvailableSerials(resp?.availableSerials || {});
      } catch (e) {
        if (cancelled) return;
        // Picker is a convenience — on any failure fall back to free-text.
        setIsWondersoftStore(false);
        setAvailableSerials({});
      } finally {
        if (!cancelled) setLoadingSerials(false);
      }
    };
    loadSerials();
    return () => {
      cancelled = true;
    };
  }, [
    showModal,
    getNewSerialNo,
    selectedParcelId,
    extraParcelIds,
    targetWarehouse,
  ]);

  // Dropdown options of in-stock serials for a product (by itemNumber).
  const serialOptions = (itemNumber) =>
    (availableSerials[itemNumber] || []).map((item) => ({
      label:
        item.stock > 1 ? `${item.serial} (stock ${item.stock})` : item.serial,
      value: item.serial,
    }));
  // For a Wondersoft-onboarded store the new serial MUST be picked from the
  // in-stock serials (never free-text), so show the dropdown whenever the store
  // is onboarded — even if the picker for this product happens to be empty. The
  // free-text box is only ever shown for non-wondersoft (Wings) stores.
  const hasSerialPicker = () => isWondersoftStore;

  const serialLoadingIndicator = (
    <div className="d-flex align-items-center mt-1">
      <Spinner animation="border" size="sm" className="me-2" />
      <span className="text-muted small">
        {t('Fetching available serials…')}
      </span>
    </div>
  );

  return (
    <Modal
      id={generateRandomId()}
      size={getNewSerialNo ? 'lg' : 'sm'}
      as={Modal.Dialog}
      centered
      show={showModal}
      onHide={closeModal}
    >
      <Modal.Header>
        <Modal.Title className="h6">{t('Reschedule Parcel')}</Modal.Title>
        <Button variant="close" aria-label="Close" onClick={closeModal} />
      </Modal.Header>
      <Modal.Body>
        {additionalParcelToChoose?.length > 0 && (
          <Form.Group>
            <Form.Label className={'mb-4'}>
              {t('Choose Extra Parcels to Schedule')}
            </Form.Label>
            {additionalParcelToChoose?.map((extraParcel) => {
              return (
                <Row>
                  <Col>
                    <Form.Check
                      defaultValue={false}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setExtraParcelIds(
                            extraParcelIds.concat([extraParcel.objectId])
                          );
                          setIsNonSerialNumberProduct((prev) => ({
                            ...prev,
                            [extraParcel.objectId]: false,
                          }));
                        } else {
                          // remove it from the list
                          setExtraParcelIds(
                            extraParcelIds.filter(
                              (parcelInfo) =>
                                parcelInfo !== extraParcel.objectId
                            )
                          );
                          setIsNonSerialNumberProduct((prev) => ({
                            ...prev,
                            [extraParcel.objectId]: false,
                          }));
                        }
                      }}
                      label={`${extraParcel.product}. Serial No: ${extraParcel.serialNo}`}
                      id={extraParcel.objectId}
                      htmlFor={extraParcel.objectId}
                    />
                  </Col>
                  {getNewSerialNo && (
                    <Col>
                      <div className="d-flex justify-content-between align-items-center">
                        <Form.Label>{t('Updated Serial Number')}</Form.Label>
                        <Form.Check
                          type="switch"
                          id={`toggle-${extraParcel.objectId}`}
                          label="Non Serial Number Product"
                          checked={
                            isNonSerialNumberProduct[extraParcel.objectId]
                          }
                          onChange={(e) => {
                            const { checked } = e.target;
                            setIsNonSerialNumberProduct((prev) => ({
                              ...prev,
                              [extraParcel.objectId]: checked,
                            }));
                            if (checked) {
                              setExtraParcelIdSerialNos((prev) => ({
                                ...prev,
                                [extraParcel.objectId]: '',
                              }));
                            }
                          }}
                        />
                      </div>

                      {loadingSerials &&
                      extraParcelIds?.indexOf(extraParcel.objectId) >= 0 &&
                      !isNonSerialNumberProduct[extraParcel.objectId] ? (
                        serialLoadingIndicator
                      ) : hasSerialPicker(extraParcel.itemNumber) &&
                        extraParcelIds?.indexOf(extraParcel.objectId) >= 0 &&
                        !isNonSerialNumberProduct[extraParcel.objectId] ? (
                        <DropdownComponent
                          options={serialOptions(extraParcel.itemNumber)}
                          placeholder={t('Select in-stock serial')}
                          value={
                            serialOptions(extraParcel.itemNumber).find(
                              (option) =>
                                option.value ===
                                extraParcelIdSerialNos[extraParcel.objectId]
                            ) || null
                          }
                          onChange={(option) => {
                            setExtraParcelIdSerialNos({
                              ...extraParcelIdSerialNos,
                              [extraParcel.objectId]: option?.value || '',
                            });
                          }}
                        />
                      ) : (
                        <Form.Control
                          disabled={
                            extraParcelIds?.indexOf(extraParcel.objectId) < 0 ||
                            isNonSerialNumberProduct[extraParcel.objectId]
                          }
                          isInvalid={
                            extraSerialNumberError?.[extraParcel.objectId]
                              ?.length > 0
                          }
                          type="text"
                          onChange={(event) => {
                            extraParcelIdSerialNos[extraParcel.objectId] =
                              event.target.value;
                            setExtraParcelIdSerialNos({
                              ...extraParcelIdSerialNos,
                            });
                          }}
                          value={extraParcelIdSerialNos[extraParcel.objectId]}
                          placeholder={t('Updated Serial No')}
                        />
                      )}

                      {extraSerialNumberError?.[extraParcel.objectId]?.length >
                        0 && (
                        <Form.Control.Feedback type="invalid">
                          {extraSerialNumberError?.[extraParcel.objectId]}
                        </Form.Control.Feedback>
                      )}
                    </Col>
                  )}
                </Row>
              );
            })}
          </Form.Group>
        )}
        <Form.Group className="mb-4">
          <Form.Label
            className={
              formErrors['targetBranch']?.length > 0 ? 'text-danger' : ''
            }
          >
            {t('Choose Target Branch')}
          </Form.Label>
          <DropdownComponent
            disabled={disableTargetDropdown}
            onChange={(option) => {
              setFormData((oldData) => ({
                ...oldData,
                targetBranch: option.value,
              }));
              // Serials are pulled from the selected target branch's inventory.
              setTargetWarehouse(option?.data || null);
            }}
            options={targetDropdownOptions}
            placeholder={t(selectedParcel?.returnScanWarehouse)}
            value={data.targetBranch}
          />
        </Form.Group>
        {getNewSerialNo && warehouse?.selectChallanBranch && (
          <Form.Group className="mb-4">
            <Form.Label
              className={
                formErrors['challanBranch']?.length > 0 ? 'text-danger' : ''
              }
            >
              {t('Choose Challan Branch')}
            </Form.Label>
            <DropdownComponent
              onChange={(option) => {
                console.log('option', option);
                setFormData((oldData) => ({
                  ...oldData,
                  challanBranch: option.data,
                }));
              }}
              options={targetDropdownOptions}
              value={data.challanBranch?.warehouseCode}
            />
          </Form.Group>
        )}
        {formErrors['challanBranch']?.length > 0 && (
          <Form.Control.Feedback type="invalid">
            {formErrors['challanBranch']}
          </Form.Control.Feedback>
        )}
        <Form.Group className="mb-4">
          <Form.Label>{t('Choose Delivery Date')}</Form.Label>
          <Datetime
            timeFormat={false}
            closeOnSelect={true}
            onChange={(value) => {
              setFormData((oldData) => ({
                ...oldData,
                date: new Date(value),
              }));
            }}
            renderInput={(props, openCalendar) => (
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="icon icon-xs"
                  />
                </InputGroup.Text>
                <Form.Control
                  isInvalid={formErrors['date']?.length > 0}
                  required
                  type="text"
                  value={returnUserReadableDate(data.date)}
                  placeholder="Select delivery date"
                  onFocus={openCalendar}
                />
                {formErrors['date']?.length > 0 && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors['date']}
                  </Form.Control.Feedback>
                )}
              </InputGroup>
            )}
          />
        </Form.Group>

        {getNewSerialNo && (
          <Form.Group className="mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label>{t('New Serial Number')}</Form.Label>
              <Form.Check
                type="switch"
                id={`toggle-${selectedParcelId}`}
                label="Non Serial Number Product"
                checked={isNonSerialNumberProduct[selectedParcelId]}
                onChange={(e) => {
                  const { checked } = e.target;
                  setIsNonSerialNumberProduct((prev) => ({
                    ...prev,
                    [selectedParcelId]: checked,
                  }));
                  if (checked) {
                    setFormData((oldData) => ({
                      ...oldData,
                      newSerialNo: '',
                    }));
                  }
                }}
              />
            </div>
            {loadingSerials ? (
              serialLoadingIndicator
            ) : hasSerialPicker(selectedParcel?.itemNumber) &&
              !isNonSerialNumberProduct[selectedParcelId] ? (
              <DropdownComponent
                options={serialOptions(selectedParcel?.itemNumber)}
                placeholder={t('Select in-stock serial')}
                value={
                  serialOptions(selectedParcel?.itemNumber).find(
                    (option) => option.value === data.newSerialNo
                  ) || null
                }
                onChange={(option) => {
                  setFormData((oldData) => ({
                    ...oldData,
                    newSerialNo: option?.value || '',
                  }));
                }}
              />
            ) : (
              <Form.Control
                isInvalid={formErrors['newSerialNo']?.length > 0}
                required
                type="text"
                onChange={(event) => {
                  const newSerialNo = event.target.value;
                  setFormData((oldData) => ({
                    ...oldData,
                    newSerialNo,
                  }));
                }}
                placeholder={t('New Serial No')}
                value={data.newSerialNo}
                disabled={isNonSerialNumberProduct[selectedParcelId]}
              />
            )}
            {formErrors['newSerialNo']?.length > 0 && (
              <Form.Control.Feedback type="invalid">
                {formErrors['newSerialNo']}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        )}
        <Form.Group className="mb-4">
          <Form.Label>{t('Reason')}</Form.Label>
          <Form.Control
            isInvalid={formErrors['remarks']?.length > 0}
            required
            type="text"
            defaultValue={data.remarks || selectedParcel?.remarks}
            onChange={(event) => {
              const remarks = event.target.value;
              setFormData((oldData) => ({
                ...oldData,
                remarks,
              }));
            }}
            placeholder={t('Reason to Reschedule')}
          />
          {formErrors['remarks']?.length > 0 && (
            <Form.Control.Feedback type="invalid">
              {formErrors['remarks']}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleConfirmClick}>
          {t('Rechedule')}
        </Button>
        <Button
          variant="link"
          className="text-gray ms-auto"
          onClick={closeModal}
        >
          {t('Cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default DefectivePickupRescheduleModal;
